import React, {
  createContext,
  useEffect,
  useRef,
  useState,
  useContext,
} from 'react';
import axios from 'axios';
import { useAuth } from 'react-oidc-context';
import { User } from 'oidc-client-ts';
import jwtDecode from 'jwt-decode';
import {
  BYMELDINGADMIN,
  BYMENTREPRENOR,
  BYMENTREPRENORADMIN,
  BYMSERVICEAVDELING,
  BYMSERVICEAVDELINGADMIN,
  DASHBOARD,
  ENTREPRENOR,
  ENTREPRENORKONTROLLADMIN,
  FORVALTER,
  KONTROLLOR,
} from '../constants/roles';
import HttpClient from '../api/httpClient';
import oidcConfig from '../oidcConfig';
import { feilInfo, sesjonsLogg, tokenInfo } from './sesjonsLogg';
import userManager from './userManager';
// import { Bruker } from '../../interfaces/userInterface';

// Kilde-agnostisk håndtering av sesjons-utløp: alle triggere (401-interceptor,
// silent-renew-feil, user-signed-out) deler én pågående signinSilent og
// maks én signinRedirect, så vi unngår redirect-storm og «blaff» i UI.
export type SigninSilentArgs = { resource?: string | string[] };

export type AuthLike = {
  signinSilent: (
    args?: SigninSilentArgs
  ) => Promise<User | null | undefined | void>;
  signinRedirect: (args?: { state?: unknown }) => Promise<void> | void;
  removeUser?: () => Promise<void>;
};

// Fallback så guarden nullstilles hvis nettleseren aldri fullførte navigasjon
// (f.eks. nettverksbrudd mot OneLogin) — ellers står monitoren evig i overlay.
const REDIRECT_GUARD_TIMEOUT_MS = 30_000;

// Et nettverksglipp på driftsskjermen skal ikke koste en manuell innlogging.
// Vi prøver på nytt med økende pause før vi gir opp og sender til OneLogin.
export const RENEW_RETRY_FORSINKELSER_MS = [2_000, 10_000, 30_000];

// Feil hvor refresh token er reelt dødt: ny innlogging er eneste vei videre.
// Alt annet (nettverk, timeout, 5xx hos OneLogin) er forbigående, og da skal
// vi IKKE slette brukeren — det ville kastet et fullt gyldig refresh token.
const UGJENKALLELIGE_FEILKODER = [
  'invalid_grant',
  'invalid_client',
  'login_required',
  'interaction_required',
  'consent_required',
  'account_selection_required',
];

export const erUgjenkalleligSesjonsfeil = (feil: unknown): boolean => {
  const kilde = feil as { error?: string; message?: string } | null;
  const tekst = `${kilde?.error ?? ''} ${kilde?.message ?? ''}`.toLowerCase();
  return UGJENKALLELIGE_FEILKODER.some((kode) => tekst.includes(kode));
};

let pagaendeSigninSilent: Promise<User | null | undefined | void> | null = null;
let harTriggetSigninRedirect = false;

// Enkel pub/sub så UI kan vise en overlay i den korte perioden mellom
// «vi ga opp silent renew» og at nettleseren faktisk navigerer til OneLogin.
type RedirectLytter = () => void;
const redirectLyttere = new Set<RedirectLytter>();

export const onRedirectingTilInnlogging = (
  fn: RedirectLytter
): (() => void) => {
  redirectLyttere.add(fn);
  return () => {
    redirectLyttere.delete(fn);
  };
};

// `resource` defaultes fra settings kun i authorization-code-flyten
// (OidcClient.createSigninRequest). UserManager.signinSilent sender videre
// nøyaktig det vi gir den — uten dette får fornyede access tokens feil `aud`,
// og API-et svarer 401 på alt mens UI-et fortsatt ser innlogget ut.
export const hentSilentArgs = (): SigninSilentArgs => ({
  resource: (oidcConfig as { resource?: string | string[] }).resource,
});

export const hentPagaendeSigninSilent = (
  auth: AuthLike
): Promise<User | null | undefined | void> => {
  if (!pagaendeSigninSilent) {
    const args = hentSilentArgs();
    sesjonsLogg('fornyelse-start', { resource: args.resource });
    pagaendeSigninSilent = auth
      .signinSilent(args)
      .then((bruker): User | null | undefined | void => {
        sesjonsLogg('fornyelse-ok', {
          nyttToken: tokenInfo(bruker as User | undefined),
        });
        return bruker;
      })
      .catch((feil): never => {
        sesjonsLogg('fornyelse-feilet', {
          ...feilInfo(feil),
          ugjenkallelig: erUgjenkalleligSesjonsfeil(feil),
        });
        throw feil;
      })
      .finally((): void => {
        pagaendeSigninSilent = null;
      });
  }
  return pagaendeSigninSilent;
};

const vent = (ms: number): Promise<void> =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

// Prøver fornyelse på nytt ved forbigående feil. Avbryter umiddelbart hvis
// feilen er ugjenkallelig — da er det bortkastet tid å vente.
export const fornySesjonMedRetry = async (
  auth: AuthLike,
  forsinkelser: number[] = RENEW_RETRY_FORSINKELSER_MS
): Promise<User | null | undefined | void> => {
  let sisteFeil: unknown = new Error('signinSilent ga ingen bruker');

  for (let i = 0; i <= forsinkelser.length; i += 1) {
    try {
      const bruker = await hentPagaendeSigninSilent(auth);
      if (bruker) {
        if (i > 0)
          sesjonsLogg('fornyelse-lyktes-etter-retry', { forsok: i + 1 });
        return bruker;
      }
      sesjonsLogg('fornyelse-ga-ingen-bruker', { forsok: i + 1 });
    } catch (feil) {
      if (erUgjenkalleligSesjonsfeil(feil)) throw feil;
      sisteFeil = feil;
    }
    if (i < forsinkelser.length) {
      sesjonsLogg('fornyelse-retry-planlagt', {
        forsok: i + 1,
        venterMs: forsinkelser[i],
      });
      await vent(forsinkelser[i]);
    }
  }

  sesjonsLogg('fornyelse-ga-opp', {
    antallForsok: forsinkelser.length + 1,
    ...feilInfo(sisteFeil),
  });
  throw sisteFeil;
};

export const trigSingleSigninRedirect = (
  auth: AuthLike,
  returnTo?: string,
  opts: { slettBruker?: boolean } = {}
): void => {
  const { slettBruker = true } = opts;
  if (harTriggetSigninRedirect) {
    sesjonsLogg('redirect-hoppet-over', { grunn: 'guard allerede aktiv' });
    return;
  }
  harTriggetSigninRedirect = true;
  sesjonsLogg('redirect-til-innlogging', { returnTo, slettBruker });
  redirectLyttere.forEach((fn) => fn());

  // Rydd localStorage — ellers tror appen ved refresh at brukeren er innlogget
  // og går rett i 401-runden igjen. Unntak: forbigående feil, hvor refresh
  // token fortsatt er gyldig og appen kan komme seg selv etterpå.
  if (slettBruker) {
    auth.removeUser?.().catch((): void => undefined);
  }

  const nullstillGuard = (): void => {
    harTriggetSigninRedirect = false;
  };
  const timeoutId = setTimeout(nullstillGuard, REDIRECT_GUARD_TIMEOUT_MS);
  const args = returnTo ? { state: { returnTo } } : undefined;
  Promise.resolve(auth.signinRedirect(args)).catch((feil): void => {
    sesjonsLogg('redirect-feilet', feilInfo(feil));
    clearTimeout(timeoutId);
    nullstillGuard();
  });
};

// Felles inngang for alle sesjonsbrudd (401-interceptor, silent-renew-feil,
// utløpt access token). Forbigående feil får retry i bakgrunnen; kun når
// refresh token er reelt dødt sender vi brukeren til OneLogin.
export const handterSesjonsbrudd = (
  auth: AuthLike,
  feil: unknown,
  returnTo?: string
): Promise<void> => {
  sesjonsLogg('sesjonsbrudd', feilInfo(feil));

  if (erUgjenkalleligSesjonsfeil(feil)) {
    trigSingleSigninRedirect(auth, returnTo);
    return Promise.resolve();
  }

  return fornySesjonMedRetry(auth)
    .then((bruker): void => {
      if (bruker)
        HttpClient.setDefaultCredentials((bruker as User).access_token);
    })
    .catch((sisteFeil): void => {
      trigSingleSigninRedirect(auth, returnTo, {
        slettBruker: erUgjenkalleligSesjonsfeil(sisteFeil),
      });
    });
};

// Kun for tester — nullstiller modul-nivå single-flight/redirect-guard.
export const _resetSessionStateForTest = (): void => {
  pagaendeSigninSilent = null;
  harTriggetSigninRedirect = false;
  redirectLyttere.clear();
};

export const hentLagretBruker = (): User | undefined => {
  const oidcStorage = localStorage.getItem(
    `oidc.user:${(oidcConfig as any).authority}:${
      (oidcConfig as any).client_id
    }`
  );
  return oidcStorage ? User.fromStorageString(oidcStorage) : undefined;
};

// Setter Authorization på hver request fra token-lageret, i stedet for å stole
// på axios.defaults. Defaults settes i useEffect i App/UserProvider, men barnas
// effekter kjører før foreldrenes — monitorens første kall gikk derfor uten
// token etter hver sideinnlasting, fikk 401 og brukte opp en refresh token-
// rotasjon helt unødvendig. Må registreres før ReactDOM.render (index.js).
// axios 1.x har allerede flettet inn defaults her, så vi kan ikke skille
// eksplisitt header fra en utdatert default — lageret vinner alltid, unntatt
// `Authorization: ''`, som betyr «send uten token» (statiske data).
export const registrerTokenPaaForesporsler = (): number =>
  axios.interceptors.request.use((config) => {
    if (config.headers.Authorization === '') return config;
    const accessToken = hentLagretBruker()?.access_token;
    if (accessToken) {
      config.headers.set('Authorization', `Bearer ${accessToken}`);
    }
    return config;
  });

// Faner deler token-lager, men hver fane har egne utløpstimere. Uten lås
// fornyer alle faner samtidig med SAMME refresh token, og OneLogin avviser
// alle unntatt én (sett i loggen 02.11 kl. 15:31:28). Med låsen venter de andre
// fanene, og ser de da at tokenet er byttet ut, bruker de det i stedet.
const FORNYELSESLAAS = 'enk-token-fornyelse';

type LagretBruker = { access_token?: string; expired?: boolean };

export const fornyMedLaas = async <T extends LagretBruker>(
  forny: () => Promise<T | null>,
  hentLagret: () => Promise<T | null>,
  tokenFoerLaas: string | undefined
): Promise<T | null> => {
  const { locks } = navigator as Navigator & { locks?: LockManager };
  if (!locks) return forny();
  return locks.request(FORNYELSESLAAS, async () => {
    const lagret = await hentLagret();
    if (
      lagret?.access_token &&
      lagret.access_token !== tokenFoerLaas &&
      !lagret.expired
    ) {
      sesjonsLogg('fornyelse-hoppet-over', {
        grunn: 'annen fane har fornyet',
        token: tokenInfo(lagret),
      });
      return lagret;
    }
    return forny();
  });
};

// react-oidc-context sin signinSilent fanger feil og returnerer null, så vi
// aldri ser invalid_grant, og den setter isLoading=true mens den kjører — da
// viser App «Vennligst vent...» og hele appen avmonteres ved hver fornyelse.
// Vi kaller derfor UserManager direkte. userLoaded oppdaterer React-state.
// getUser() i låsen re-armerer utløpstimerne i denne fanen.
export const medDirekteFornyelse = (auth: AuthLike): AuthLike => ({
  signinSilent: (args) =>
    fornyMedLaas(
      () => userManager.signinSilent(args),
      () => userManager.getUser(),
      hentLagretBruker()?.access_token
    ),
  signinRedirect: (args) => auth.signinRedirect(args),
  removeUser: () => userManager.removeUser(),
});

export interface Bruker {
  brukerId: string;
  navn: string;
  epost: string;
  roller: string[];
}

const initBruker: Bruker = {
  brukerId: '',
  navn: '',
  epost: '',
  roller: [],
};

interface IUserContext {
  user: Bruker;
  login: () => void;
  logout: () => void;
  getName: () => string;
  getId: () => string;
  getRoles: () => string[];
  getPrimaryRoleName: () => string;
  isLoggedIn: () => boolean;
  isBymeldingAdmin: () => boolean;
  isBymServiceavdeling: () => boolean;
  isBymServiceavdelingAdmin: () => boolean;
  isBymEntreprenor: () => boolean;
  isBymEntreprenorAdmin: () => boolean;
  isEntreprenor: () => boolean;
  isKontrollor: () => boolean;
  isForvalter: () => boolean;
  isEnkAdmin: () => boolean;
  isDashboard: () => boolean;
  isInternBymbruker: () => boolean;
}

export const UserContext = createContext({} as IUserContext);

const UserProvider = (props: { children: JSX.Element }): JSX.Element => {
  const { children } = props;
  const [user, setUser] = useState(initBruker);
  const auth = useAuth();
  const authRef = useRef(auth);

  useEffect((): void => {
    authRef.current = auth;
  }, [auth]);

  const sesjonsAuth = (): AuthLike => medDirekteFornyelse(authRef.current);

  const login = (): void => {
    auth.signinRedirect({ state: { returnTo: window.location.href } });
  };

  const logout = (): void => {
    // OneLogin krever id_token_hint når post_logout_redirect_uri er satt.
    // Hvis vi ikke har id_token (sesjonen er allerede utløpt), rydder vi
    // lokalt og navigerer selv i stedet for å treffe end_session-endepunktet.
    const idToken = auth.user?.id_token;
    if (idToken) {
      auth.signoutRedirect({ id_token_hint: idToken });
      return;
    }
    auth.removeUser().catch((): void => undefined);
    const postLogoutUri =
      (oidcConfig as any).post_logout_redirect_uri ?? '/logout';
    window.location.assign(postLogoutUri);
  };

  const getName = (): string => user?.navn;

  const getId = (): string => {
    const bymId = (auth?.user?.profile?.params as any)?.['bym-id'];
    return bymId ? String(bymId) : '';
  };

  const getToken = hentLagretBruker;

  const getRoles = (): string[] => {
    const brukertoken = getToken();
    if (!brukertoken?.access_token) return [];

    const accessToken = jwtDecode<any>(brukertoken.access_token);

    return accessToken.roles ?? [];
  };

  const HarBrukerRolle = (rolle: string): boolean => {
    const roller = getRoles();
    const rolleFinnes = roller.indexOf(rolle) > -1;
    // console.log(
    //   `Rolle ${rolle} ble ${rolleFinnes ? '' : 'IKKE '}funnet i tokenet`
    // );
    return rolleFinnes; // bare debug
  };

  const isLoggedIn = (): boolean => auth?.isAuthenticated;

  const isBymeldingAdmin = (): boolean => HarBrukerRolle(BYMELDINGADMIN);

  const isBymServiceavdeling = (): boolean =>
    HarBrukerRolle(BYMSERVICEAVDELING);

  const isBymServiceavdelingAdmin = (): boolean =>
    HarBrukerRolle(BYMSERVICEAVDELINGADMIN);

  const isBymEntreprenorAdmin = (): boolean =>
    HarBrukerRolle(BYMENTREPRENORADMIN);

  const isBymEntreprenor = (): boolean => HarBrukerRolle(BYMENTREPRENOR);

  const isEntreprenor = (): boolean => HarBrukerRolle(ENTREPRENOR);
  const isKontrollor = (): boolean => HarBrukerRolle(KONTROLLOR);
  const isForvalter = (): boolean => HarBrukerRolle(FORVALTER);
  const isEnkAdmin = (): boolean => HarBrukerRolle(ENTREPRENORKONTROLLADMIN);
  const isDashboard = (): boolean => HarBrukerRolle(DASHBOARD);

  const isInternBymbruker = (): boolean =>
    isKontrollor() ||
    isForvalter() ||
    isEnkAdmin() ||
    isBymeldingAdmin() ||
    isBymServiceavdelingAdmin() ||
    isBymServiceavdeling() ||
    isDashboard();

  const getPrimaryRoleName = () => {
    // for bakoverkompatibilitet må vi returnere "primærrollen" til bruker,
    // dvs den høyeste tilgangen vedkommende har.
    if (isBymeldingAdmin()) return 'Bymelding Admin';
    if (isEnkAdmin()) return 'Entreprenørkontroll Admin';
    if (isBymServiceavdelingAdmin()) return 'Serviceavdeling admin';
    if (isBymServiceavdeling()) return 'Serviceavdeling';
    if (isBymEntreprenorAdmin()) return 'Bymelding entreprenøradmin';
    if (isBymEntreprenor()) return 'Bymelding entreprenør';
    if (isEntreprenor()) return 'Entreprenør';
    if (isKontrollor()) return 'Kontrollør';
    if (isForvalter()) return 'Forvalter';
    if (isDashboard()) return 'Dashboard';

    return 'Ukjent';
  };

  useEffect((): void => {
    if (!auth.isAuthenticated) return;

    setUser({
      brukerId: getId() ?? '',
      navn: auth.user?.profile.name ?? '',
      epost: auth.user?.profile.email ?? '',
      roller: getRoles(),
    });

    HttpClient.setDefaultCredentials(getToken()?.access_token);
  }, [auth, auth.isAuthenticated]);

  // Update the axios Authorization header whenever a silent renew loads a new token,
  // so API calls never use a stale token even when the renewal happens in the background.
  useEffect((): (() => void) => {
    const handleUserLoaded = (loadedUser: User): void => {
      sesjonsLogg('bruker-lastet', {
        token: tokenInfo(loadedUser),
      });
      HttpClient.setDefaultCredentials(loadedUser.access_token);
    };

    const handleSilentRenewError = (feil?: unknown): void => {
      sesjonsLogg('event:silentRenewError', feilInfo(feil));
      handterSesjonsbrudd(sesjonsAuth(), feil, window.location.href);
    };

    const handleUserSignedOut = (): void => {
      // Sesjonen er avsluttet hos OneLogin — ny innlogging er eneste vei.
      sesjonsLogg('event:userSignedOut');
      trigSingleSigninRedirect(sesjonsAuth(), window.location.href);
    };

    // Vi driver fornyelsen selv i stedet for `automaticSilentRenew`, fordi
    // SilentRenewService kaller signinSilent() uten argumenter og dermed
    // mister `resource` — se hentSilentArgs().
    const handleAccessTokenExpiring = (): void => {
      sesjonsLogg('event:accessTokenExpiring', {
        token: tokenInfo(authRef.current?.user),
      });
      handterSesjonsbrudd(sesjonsAuth(), undefined, window.location.href);
    };

    const handleAccessTokenExpired = (): void => {
      sesjonsLogg('event:accessTokenExpired');
      handterSesjonsbrudd(sesjonsAuth(), undefined, window.location.href);
    };

    auth.events.addUserLoaded(handleUserLoaded);
    auth.events.addSilentRenewError(handleSilentRenewError);
    auth.events.addUserSignedOut(handleUserSignedOut);
    auth.events.addAccessTokenExpiring(handleAccessTokenExpiring);
    auth.events.addAccessTokenExpired(handleAccessTokenExpired);

    return (): void => {
      auth.events.removeUserLoaded(handleUserLoaded);
      auth.events.removeSilentRenewError(handleSilentRenewError);
      auth.events.removeUserSignedOut(handleUserSignedOut);
      auth.events.removeAccessTokenExpiring(handleAccessTokenExpiring);
      auth.events.removeAccessTokenExpired(handleAccessTokenExpired);
    };
  }, [auth]);

  // Intercept 401 responses: silently refresh the token and retry the request once.
  // This handles the case where the token expires mid-session and the UI still
  // appears logged in but all API calls are failing.
  useEffect((): (() => void) => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        if (
          error.response?.status !== 401 ||
          !originalRequest ||
          originalRequest.retried ||
          originalRequest._skipAuthRefresh
        ) {
          return Promise.reject(error);
        }

        originalRequest.retried = true;
        sesjonsLogg('api-401', {
          url: originalRequest.url,
          // Skiller «token avvist» fra «token aldri sendt» (ENK-29, §7).
          harAuthHeader: Boolean(originalRequest.headers?.Authorization),
          token: tokenInfo(authRef.current?.user),
        });

        try {
          const refreshedUser = await hentPagaendeSigninSilent(sesjonsAuth());
          if (!refreshedUser) {
            // Ingen ny bruker: la retry-logikken jobbe videre i bakgrunnen
            // i stedet for å sende monitoren rett til innlogging.
            handterSesjonsbrudd(sesjonsAuth(), undefined, window.location.href);
            return Promise.reject(error);
          }
          sesjonsLogg('api-401-retry', { url: originalRequest.url });
          HttpClient.setDefaultCredentials(refreshedUser.access_token);
          originalRequest.headers = {
            ...originalRequest.headers,
            Authorization: `Bearer ${refreshedUser.access_token}`,
          };
          return axios(originalRequest).then(
            (svar) => {
              sesjonsLogg('api-401-retry-ok', { url: originalRequest.url });
              return svar;
            },
            (retryFeil) => {
              sesjonsLogg('api-401-retry-feilet', {
                url: originalRequest.url,
                status: retryFeil?.response?.status,
              });
              throw retryFeil;
            }
          );
        } catch (renewFeil) {
          handterSesjonsbrudd(sesjonsAuth(), renewFeil, window.location.href);
          return Promise.reject(error);
        }
      }
    );

    return (): void => {
      axios.interceptors.response.eject(interceptorId);
    };
  }, []);

  return (
    <UserContext.Provider
      value={{
        user,
        login,
        logout,
        getName,
        getId,
        getRoles,
        getPrimaryRoleName,
        isLoggedIn,
        isBymeldingAdmin,
        isBymServiceavdeling,
        isBymServiceavdelingAdmin,
        isBymEntreprenor,
        isBymEntreprenorAdmin,
        isEntreprenor,
        isKontrollor,
        isForvalter,
        isEnkAdmin,
        isDashboard,
        isInternBymbruker,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export default UserProvider;

export const useUser = (): IUserContext => useContext(UserContext);
